/* eslint-disable react-hooks/exhaustive-deps */
import React, { FunctionComponent, ReactElement, useEffect, useState } from 'react';
import { SbBlokData } from '@storyblok/react';
import { graphql, useStaticQuery } from 'gatsby';

import { Button, Carousel, TabContainer, TabHeader } from '@savebetter/components';

import {
  Category,
  filterBlogPosts,
  getBlogPosts,
  getCategoryPostsCountObject,
  getSortedCategories,
  getTabSettings,
  stringToSlug
} from '../utils';

import BlogPost from './BlogPost';
import { MediaProps } from './Media';
import { PageSectionBlok, PageSectionProps } from './PageSection';
import { PageSectionHeader } from './PageSectionHeader';
import { Section } from './Section';

export interface BlogPostPageProps {
  full_slug: string;
  title: string;
  image: MediaProps[];
  body: SbBlokData[];
  created_at: string;
  published_at: string;
  first_published_at: string;
  intro?: string;
  categories?: string[];
  published: boolean;
}

export interface BlogPostCustomData {
  title: string;
  description: string;
  media: MediaProps[];
}

interface Blok extends PageSectionBlok {
  sectionTitle: string;
  blogPosts: Array<string>;
  blogPostCustomData: BlogPostCustomData[];
  loadMoreItemsBtnText?: string;
  sort: 'created_at' | 'published_at' | 'first_published_at';
  sortDirection: 'asc' | 'desc';
}

interface HomePageFilteredBlogPostsProps extends PageSectionProps {
  blok: Blok;
}

interface AllBlogPostPages {
  edges: [
    {
      node: {
        uuid: string;
        content: string;
        full_slug: string;
        created_at: string;
        published_at: string;
        first_published_at: string;
        published: boolean;
      };
    }
  ];
}

export interface AllCategories {
  edges: [
    {
      node: Category;
    }
  ];
}

interface AllStoryblokEntry {
  allCategories: AllCategories;
  allBlogPosts: AllBlogPostPages;
}

export const FilteredBlogPosts: FunctionComponent<
  React.PropsWithChildren<React.PropsWithChildren<React.PropsWithChildren<HomePageFilteredBlogPostsProps>>>
> = ({ blok }) => {
  const allStoryblokEntry: AllStoryblokEntry = useStaticQuery(
    graphql`
      query filteredBlogPostsAndCategoriesQuery {
        allBlogPosts: allStoryblokEntry(
          filter: { field_component: { eq: "blogPostPage" }, content: { regex: "/categories/" } }
        ) {
          edges {
            node {
              uuid
              content
              full_slug
              created_at
              published_at
              first_published_at
            }
          }
        }
        allCategories: allStoryblokEntry(
          filter: {
            full_slug: { regex: "/^categories//" }
            published_at: { ne: null }
            field_visible_boolean: { eq: true }
          }
        ) {
          edges {
            node {
              name
              uuid
              slug
            }
          }
        }
      }
    `
  );
  const {
    sectionTitle,
    hasBackground,
    hasBorder,
    blogPostCustomData,
    hasPaddingBottom,
    hasPaddingTop,
    loadMoreItemsBtnText,
    sort: sortField,
    sortDirection
  } = blok;
  const displayedCategoryArticlesPerLoad = 6;
  const { allBlogPosts, allCategories } = allStoryblokEntry;
  const blogPostCategories = getSortedCategories(allCategories, 'featured-articles', allCategories.edges.length);
  const blogPostCategoriesCount = blogPostCategories.length;

  const homeBlogPostsData = allBlogPosts.edges.filter(edge => edge.node);
  const homeBlogPosts: BlogPostPageProps[] = getBlogPosts(homeBlogPostsData);

  const [displayedCategoryArticles, setDisplayedCategoryArticles] = useState(displayedCategoryArticlesPerLoad);
  const [activeCategoryTab, setActiveCategoryTab] = useState<Category>(blogPostCategories[0]);
  const [totalCategoryArticles, setTotalCategoryArticles] = useState<{ [key: string]: number }>();

  const tabCarouselSettings = getTabSettings(blogPostCategoriesCount);

  const loadMoreCategoryArticles = (): void => {
    if (totalCategoryArticles && totalCategoryArticles[activeCategoryTab.slug] > displayedCategoryArticles) {
      setDisplayedCategoryArticles(displayedCategoryArticles + displayedCategoryArticlesPerLoad);
    }
  };

  useEffect(() => {
    if (!totalCategoryArticles) {
      const counterObj = getCategoryPostsCountObject(blogPostCategories, homeBlogPosts);

      setTotalCategoryArticles(counterObj);
    }
  }, [blogPostCategories]);

  const getTabHeaders = (): ReactElement[] =>
    blogPostCategories.map(({ name, slug, uuid }) => (
      <TabHeader
        isActive={activeCategoryTab.slug === slug}
        onClick={(): void => {
          setActiveCategoryTab({ name, uuid, slug });
        }}
        title={name}
        alt={name}
        key={uuid}
        isCircleImg={false}
      />
    ));

  return (
    <Section
      width="md"
      hasBackground={hasBackground}
      hasPaddingBottom={hasPaddingBottom}
      hasPaddingTop={hasPaddingTop}
      hasBorder={hasBorder}
      blok={blok}
    >
      <PageSectionHeader className="md:pb-18 sm:text-center sm:pb-9">{sectionTitle}</PageSectionHeader>

      <div className="flex-1">
        <Carousel
          settings={tabCarouselSettings}
          contentLabel="Category type tabs"
          showIndicators={false}
          useDefaultSlideWrapper={false}
          slides={getTabHeaders()}
          className={'border-b-black-10 border-b-2 md:mx-12'}
        />
        <PageSectionHeader className="md:p-18 sm:text-center sm:p-9">{activeCategoryTab.name}</PageSectionHeader>
        <TabContainer visible={true} className="grid grid-cols-1 md:grid-cols-3">
          {filterBlogPosts(homeBlogPosts, activeCategoryTab, displayedCategoryArticles, sortField, sortDirection).map(
            (blogPost, index) => {
              const { title, image, intro, full_slug } = blogPost;
              const blogPostData = homeBlogPostsData.find(postData => postData.node.full_slug === full_slug);
              const postCustomData = blogPostCustomData.find(blogCustomData =>
                full_slug.includes(stringToSlug(blogCustomData.title))
              );

              return blogPostData ? (
                <BlogPost
                  key={stringToSlug(title)}
                  index={index}
                  title={title}
                  image={image}
                  intro={intro}
                  blogPostCustomData={postCustomData}
                  blogPostData={blogPostData}
                />
              ) : null;
            }
          )}
        </TabContainer>
        {totalCategoryArticles && totalCategoryArticles[activeCategoryTab.slug] > displayedCategoryArticles && (
          <Button onClick={(): void => loadMoreCategoryArticles()} kind="secondary" className="my-16 mx-auto">
            {loadMoreItemsBtnText ? loadMoreItemsBtnText : 'More Articles'}
          </Button>
        )}
      </div>
    </Section>
  );
};
